/**
 * Builds the props for <bo-price-display> on a catalog product card: price, tax tag,
 * original price and discount steps. Shared by the desktop and mobile catalog lists and
 * the PDP "Also Available As" bundle cards, so a product is priced the same everywhere.
 *
 * Points are left to the caller because they depend on the viewer's point config.
 */
const { formatLineDiscountSteps } = require("./line-discount-label");

function applyDisplayTax(price, taxClass, { taxExempted, taxIncluded, displayIncludingTax }) {
    if (taxExempted) {
        return taxIncluded && taxClass ? price / (1 + taxClass.percentage / 100) : price;
    }

    // No tax class, or stored and displayed on the same basis: show the price as is.
    if (!taxClass || taxIncluded == displayIncludingTax) {
        return price;
    }

    if (taxIncluded && !displayIncludingTax) {
        return price / (1 + taxClass.percentage / 100);
    }

    return price * (1 + taxClass.percentage / 100);
}

function hasProductDiscount(product) {
    return (
        (product.discount != null && Number(product.discount) > 0) ||
        (product.sale_price != null &&
            product.regular_price != null &&
            Number(product.sale_price) < Number(product.regular_price))
    );
}

function getProductDiscountedPrice(product) {
    if (product.discount != null && Number(product.discount) > 0) {
        return product.regular_price * ((100 - product.discount) / 100);
    }

    return product.sale_price ?? product.regular_price;
}

/**
 * @param {object} product  catalog-shaped product (ProductTransformer::transformForProduct)
 * @param {object} options
 * @param {boolean} options.taxExempted
 * @param {boolean} options.taxIncluded           store prices include tax
 * @param {boolean} options.displayIncludingTax   display prices including tax
 * @param {boolean} options.hideLineItemTax
 * @param {*} options.currencySymbol              falls back to "RM" when not a string
 * @param {string} options.fromLabel              prefix for a bundle price range
 */
function buildCatalogPriceDisplay(product, options) {
    const { taxExempted, hideLineItemTax, fromLabel } = options;
    const currencySymbol = typeof options.currencySymbol === "string" ? options.currencySymbol : "RM";
    const withTax = (price) => applyDisplayTax(price, product.tax_class, options);
    const isBundle = product.type == "bundle";

    let display;
    if (hasProductDiscount(product)) {
        const discountedPrice = getProductDiscountedPrice(product);
        display = {
            price: withTax(discountedPrice),
            originalPrice: withTax(product.regular_price),
            discountSteps: formatLineDiscountSteps({
                formula: product.discount_formula,
                unitPrice: discountedPrice,
                regularPrice: product.regular_price,
                currencySymbol,
            }),
        };
    } else if (product.min_price != null && product.max_price != null && product.min_price != product.max_price) {
        display = isBundle
            ? { price: withTax(product.min_price), prefix: fromLabel ?? "From" }
            : { price: withTax(product.min_price), maxPrice: withTax(product.max_price) };
    } else {
        display = { price: withTax(product.sale_price ?? product.regular_price) };
    }

    // A bundle parent cannot have multi-level discounts: always the effective percentage.
    if (isBundle && product.original_min_price && product.min_price != product.original_min_price) {
        display.originalPrice = withTax(product.original_min_price);
        display.discountSteps = formatLineDiscountSteps({
            formula: null,
            unitPrice: product.min_price,
            regularPrice: product.original_min_price,
        });
    }

    return {
        ...display,
        currencySymbol,
        taxLabel:
            product.tax_class && !taxExempted && product.tax_class.visible && !hideLineItemTax
                ? product.tax_class.name
                : null,
    };
}

module.exports = { applyDisplayTax, hasProductDiscount, getProductDiscountedPrice, buildCatalogPriceDisplay };
